import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { PERMISSIONS } from "@/constants/permissions"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { RefreshCw } from "lucide-react"
import { useState } from "react"
import { DocumentPreview } from "./DocumentPreview"
import { catalog, curation, type DocumentKind, type SavedDocument } from "./api"

const kinds: DocumentKind[] = [
  "unknown",
  "reach",
  "rohs",
  "conflict_minerals",
  "material_declaration",
  "conformity",
  "certificate",
  "other",
]
const statusKeys: Record<string, TranslationKeys> = {
  unknown: "CI_UNKNOWN",
  reach: "CI_KIND_REACH",
  rohs: "CI_KIND_ROHS",
  conflict_minerals: "CI_KIND_CONFLICT_MINERALS",
  material_declaration: "CI_KIND_MATERIAL_DECLARATION",
  conformity: "CI_KIND_CONFORMITY",
  certificate: "CI_KIND_CERTIFICATE",
  other: "CI_KIND_OTHER",
  approved: "CI_REVIEWED",
  rejected: "CI_REJECTED",
  unreviewed: "CI_NEEDS_REVIEW",
  not_checked: "CI_PENDING",
  unchanged: "CI_SOURCE_UNCHANGED",
  changed: "CI_SOURCE_CHANGED",
  failed: "CI_SOURCE_FAILED",
  suggested: "CI_SUGGESTED",
  needs_review: "CI_NEEDS_REVIEW",
}
const key = (id: string) => ["company-index", "curation", id]

export function CurationBadge({
  document,
}: {
  document: Pick<SavedDocument, "decision" | "needsAttention" | "overdue">
}) {
  const { translate: t } = useTranslation()
  return (
    <Badge
      variant={
        document.needsAttention || document.overdue
          ? "destructive"
          : "secondary"
      }
    >
      {document.needsAttention
        ? t("CI_SOURCE_NEEDS_REVIEW")
        : document.overdue
          ? t("CI_CHECK_OVERDUE")
          : document.decision === "approved"
            ? t("CI_REVIEWED")
            : document.decision === "rejected"
              ? t("CI_REJECTED")
              : t("CI_NEEDS_REVIEW")}
    </Badge>
  )
}

export function CurationPanel({
  id,
  contentType,
  view = "review",
}: {
  id: string
  contentType: string
  view?: "review" | "sources" | "history"
}) {
  const { user, hasPermission } = useUser()
  const { translate: t } = useTranslation()
  const label = (value: string) =>
    statusKeys[value] ? t(statusKeys[value]) : value.replaceAll("_", " ")
  const canEdit = hasPermission(PERMISSIONS.MC_DECLARATIONS_UPLOAD)
  const client = useQueryClient()
  const state = useQuery({
    queryKey: key(id),
    queryFn: () => curation.state(id),
    refetchInterval: view === "sources" ? 5000 : false,
  })
  const recipes = useQuery({
    queryKey: ["company-index", "recipes", id],
    queryFn: () => catalog.recipes(id),
  })
  const [preview, setPreview] = useState<{
    page: number
    quote?: string
  } | null>(null)
  const showSources = view === "sources"
  const showHistory = view === "history"
  const history = useQuery({
    queryKey: ["company-index", "curation-history", id],
    queryFn: () => curation.history(id),
    enabled: showHistory,
  })
  const sources = useInfiniteQuery({
    queryKey: ["company-index", "curation-sources", id],
    queryFn: ({ pageParam }) => catalog.observations(id, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: showSources,
  })
  const sourceItems = sources.data?.pages.flatMap((page) => page.items) ?? []
  const [kind, setKind] = useState<DocumentKind>("unknown")
  const [reason, setReason] = useState("")
  const [recipeId, setRecipeId] = useState("")
  const [confirmed, setConfirmed] = useState(false)
  const [sourceFailureAcknowledged, setSourceFailureAcknowledged] =
    useState(false)
  const selected =
    recipeId || state.data?.schedule?.recipeId || recipes.data?.[0]?.id || ""
  const refresh = () => {
    void client.invalidateQueries({ queryKey: key(id) })
    void client.invalidateQueries({
      queryKey: ["company-index", user?.organization, "attention"],
    })
    void client.invalidateQueries({
      queryKey: ["company-index", user?.organization, "document"],
    })
    void client.invalidateQueries({
      queryKey: ["company-index", user?.organization, "documents"],
    })
    void client.invalidateQueries({
      queryKey: ["company-index", "recipes", id],
    })
    void client.invalidateQueries({
      queryKey: ["company-index", "curation-sources", id],
    })
    void client.invalidateQueries({
      queryKey: ["company-index", "curation-history", id],
    })
  }
  const classify = useMutation({
    mutationFn: () => curation.classify(id, state.data!.revision),
    onSuccess: refresh,
  })
  const review = useMutation({
    mutationFn: (decision: "approved" | "rejected") =>
      curation.review(id, {
        expectedRevision: state.data!.revision,
        decision,
        kind,
        reason,
        reviewed: confirmed,
        sourceFailureAcknowledged:
          decision === "approved" && sourceFailureAcknowledged,
      }),
    onSuccess: () => {
      refresh()
      setConfirmed(false)
      setSourceFailureAcknowledged(false)
    },
  })
  const check = useMutation({
    mutationFn: () =>
      curation.queueCheck(
        id,
        selected
          ? {
              expectedRevision: state.data!.schedule?.revision ?? 0,
              recipeId: selected,
              enabled: false,
            }
          : undefined,
      ),
    onSuccess: refresh,
  })
  const monitor = useMutation({
    mutationFn: (enabled: boolean) =>
      curation.monitor(id, {
        expectedRevision: state.data!.schedule?.revision ?? 0,
        recipeId: selected,
        enabled,
      }),
    onSuccess: refresh,
  })
  const error = [classify, review, check, monitor].find(
    (action) => action.isError,
  )
  if (state.isPending) return <p>{t("CI_LOADING")}</p>
  if (state.isError || !state.data)
    return <Button onClick={() => void state.refetch()}>{t("CI_RETRY")}</Button>
  const entry = state.data
  return (
    <div className="space-y-4 rounded-lg border border-border p-4">
      <div className="flex flex-wrap items-center gap-3">
        <strong>{t("CI_DOCUMENT_REVIEW")}</strong>
        <CurationBadge document={entry} />
        <span className="text-muted-foreground">
          {t("CI_SOURCE_STATUS")}: {label(entry.retrievalStatus)}
        </span>
      </div>
      {entry.needsAttention && (
        <p role="alert" className="text-destructive">
          {t("CI_CHANGED_REVIEW_NOTE")}
        </p>
      )}
      {entry.overdue && <p role="alert">{t("CI_CHECK_OVERDUE_NOTE")}</p>}
      {view === "review" && (
        <>
          {entry.suggestion ? (
            <div className="space-y-1">
              <p>
                {t("CI_SUGGESTED_TYPE")}: {label(entry.suggestion.kind)} ·{" "}
                {label(entry.suggestion.status)}
              </p>
              <p>{entry.suggestion.reason}</p>
              {entry.suggestion.evidence.map((e, index) => (
                <blockquote
                  key={index}
                  className="border-l-2 pl-3 text-muted-foreground"
                >
                  {t("CI_PAGE")} {e.page}: “{e.quote}”
                  {contentType === "application/pdf" && (
                    <Button
                      variant="link"
                      onClick={() =>
                        setPreview({ page: e.page, quote: e.quote })
                      }
                    >
                      {t("CI_VIEW_EVIDENCE")}
                    </Button>
                  )}
                </blockquote>
              ))}
              <p className="text-xs text-muted-foreground">
                {t("CI_MODEL")}: {entry.suggestion.model ?? t("CI_UNAVAILABLE")}{" "}
                · {entry.suggestion.promptVersion} · SHA-256{" "}
                {entry.suggestion.sha256}
              </p>
            </div>
          ) : (
            <p>{t("CI_CLASSIFICATION_PENDING")}</p>
          )}
          {contentType === "application/pdf" && (
            <Button
              variant="outline"
              aria-expanded={!!preview}
              onClick={() => setPreview(preview ? null : { page: 1 })}
            >
              {t(preview ? "CI_CLOSE_PDF" : "CI_VIEW_PDF")}
            </Button>
          )}
          {preview && (
            <DocumentPreview
              key={id}
              id={id}
              page={preview.page}
              quote={preview.quote}
            />
          )}
          {canEdit && (
            <Button
              variant="outline"
              disabled={classify.isPending}
              onClick={() => classify.mutate()}
            >
              {t("CI_SUGGEST_TYPE")}
            </Button>
          )}
          {entry.decision !== "unreviewed" && (
            <p>
              {t("CI_DOCUMENT_REVIEW")}: {label(entry.decision)} ·{" "}
              {label(entry.kind)} · {entry.reason} · {entry.reviewedBy}
            </p>
          )}
          {canEdit && (
            <div className="space-y-3 border-t border-border pt-3">
              <label className="block">
                {t("CI_DOCUMENT_TYPE")}
                <select
                  aria-label={t("CI_DOCUMENT_TYPE")}
                  className="mt-1 block w-full rounded border border-border bg-background p-2"
                  value={kind}
                  onChange={(e) => setKind(e.target.value as DocumentKind)}
                >
                  {kinds.map((value) => (
                    <option key={value} value={value}>
                      {label(value)}
                    </option>
                  ))}
                </select>
              </label>
              <Input
                aria-label={t("CI_REVIEW_REASON")}
                placeholder={t("CI_TYPE_REASON_HINT")}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <label className="flex gap-2">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
                {t("CI_CONFIRM_SAVED_SOURCE")}
              </label>
              {entry.needsAttention && entry.retrievalStatus === "failed" && (
                <label className="flex gap-2">
                  <input
                    type="checkbox"
                    checked={sourceFailureAcknowledged}
                    onChange={(e) =>
                      setSourceFailureAcknowledged(e.target.checked)
                    }
                  />
                  {t("CI_ACKNOWLEDGE_SOURCE_FAILURE")}
                </label>
              )}
              <div className="flex gap-2">
                <Button
                  disabled={
                    !confirmed ||
                    !reason.trim() ||
                    kind === "unknown" ||
                    (entry.needsAttention &&
                      (entry.retrievalStatus === "changed" ||
                        (entry.retrievalStatus === "failed" &&
                          !sourceFailureAcknowledged))) ||
                    review.isPending
                  }
                  onClick={() => review.mutate("approved")}
                >
                  {t("CI_APPROVE_ENTRY")}
                </Button>
                <Button
                  variant="outline"
                  disabled={!confirmed || !reason.trim() || review.isPending}
                  onClick={() => review.mutate("rejected")}
                >
                  {t("CI_REJECT_ENTRY")}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
      {showHistory && (
        <div>
          <h4 className="font-medium">{t("CI_REVIEW_HISTORY")}</h4>
          {history.isPending && <p>{t("CI_LOADING")}</p>}
          {history.isSuccess && !history.data.length && (
            <p>{t("CI_HISTORY_EMPTY")}</p>
          )}
          {showHistory && history.isError && (
            <p role="alert">{t("CI_HISTORY_ERROR")}</p>
          )}
          {showHistory &&
            history.data?.map((event) => (
              <p key={event.revision} className="text-sm text-muted-foreground">
                {new Date(event.createdAt).toLocaleString()} · {event.actor} ·{" "}
                {label(event.details.action)}
              </p>
            ))}
        </div>
      )}
      {showSources && (
        <div className="space-y-3 border-t border-border pt-3">
          <strong>{t("CI_SOURCE_CHECKS")}</strong>
          <p className="text-sm text-muted-foreground">{t("CI_WEEKLY_NOTE")}</p>
          {![
            "application/pdf",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "application/vnd.ms-excel.sheet.macroEnabled.12",
          ].includes(contentType) && <p>{t("CI_PDF_ONLY")}</p>}
          {!!recipes.data?.length && (
            <>
              <label className="block">
                {t("CI_SAVED_SOURCE")}
                <select
                  aria-label={t("CI_SAVED_SOURCE")}
                  className="mt-1 block w-full rounded border border-border bg-background p-2"
                  value={selected}
                  onChange={(e) => setRecipeId(e.target.value)}
                >
                  {recipes.data.map((recipe) => (
                    <option key={recipe.id} value={recipe.id}>
                      {recipe.landingUrl} → {recipe.documentUrl}
                    </option>
                  ))}
                </select>
              </label>
              {canEdit && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    disabled={
                      monitor.isPending ||
                      check.isPending ||
                      entry.schedule?.running
                    }
                    onClick={() => monitor.mutate(!entry.schedule?.enabled)}
                  >
                    {entry.schedule?.enabled
                      ? t("CI_STOP_WEEKLY")
                      : t("CI_START_WEEKLY")}
                  </Button>
                </div>
              )}
            </>
          )}
          {canEdit &&
            [
              "application/pdf",
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              "application/vnd.ms-excel.sheet.macroEnabled.12",
            ].includes(contentType) && (
              <Button
                variant="outline"
                disabled={
                  check.isPending ||
                  entry.schedule?.requested ||
                  entry.schedule?.running
                }
                onClick={() => check.mutate()}
              >
                <RefreshCw className="size-4" />
                {t("CI_CHECK_SOURCE_NOW")}
              </Button>
            )}
          {recipes.isSuccess &&
            !recipes.data.length &&
            sources.isSuccess &&
            !sourceItems.some((source) => source.replayEligible) && (
              <p className="text-muted-foreground">
                {t("CI_CHECK_SETUP_HINT")}
              </p>
            )}
          {sources.isPending && <p>{t("CI_LOADING")}</p>}
          {sources.isError && (
            <p role="alert">
              {t("CI_ERROR")}{" "}
              <Button onClick={() => void sources.refetch()}>
                {t("CI_RETRY")}
              </Button>
            </p>
          )}
          {sources.isSuccess && !sourceItems.length && (
            <p>{t("CI_NO_SOURCE")}</p>
          )}
          {sourceItems.map((source) => (
            <div key={source.id} className="flex flex-wrap items-center gap-2">
              <div className="space-y-1 min-w-0 flex-1 text-sm">
                <p>
                  {source.filename} ·{" "}
                  {new Date(
                    source.observedAt ?? source.createdAt,
                  ).toLocaleString()}
                </p>
                {source.productPage && (
                  <a
                    className="block break-all underline"
                    href={source.productPage}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("CI_PRODUCT_PAGE")}: {source.productPage}
                  </a>
                )}
                {source.sourceUrl && (
                  <a
                    className="block break-all underline"
                    href={source.sourceUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("CI_SOURCE")}: {source.sourceUrl}
                  </a>
                )}
                {!source.replayEligible && (
                  <p className="text-muted-foreground">
                    {t("CI_RECAPTURE_SOURCE")}
                  </p>
                )}
              </div>
            </div>
          ))}
          {sources.hasNextPage && (
            <Button
              variant="outline"
              disabled={sources.isFetchingNextPage}
              onClick={() => void sources.fetchNextPage()}
            >
              {t("CI_MORE_SOURCES")}
            </Button>
          )}
          {entry.schedule?.requested && (
            <p role="status">{t("CI_CHECK_QUEUE_HINT")}</p>
          )}
          {entry.schedule?.running && (
            <p role="status">{t("CI_CHECKS_RUNNING")}</p>
          )}
          {entry.schedule?.enabled && (
            <p>
              {t("CI_NEXT_CHECK")}:{" "}
              {new Date(entry.schedule.nextDue).toLocaleString()}
            </p>
          )}
          {entry.runs.slice(0, 5).map((run) => (
            <p key={run.id} className="text-sm">
              {new Date(run.createdAt).toLocaleString()} · {label(run.status)}{" "}
              {run.errorCode && `· ${label(run.errorCode)}`}
            </p>
          ))}
        </div>
      )}
      {error && (
        <p role="alert" className="text-destructive">
          {t("CI_ACTION_FAILED")}
        </p>
      )}
    </div>
  )
}
